import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  InputGroup,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { FEATURES_LISTING_API } from 'constants/endpoints';
import useApi from 'hooks/useApi';
import Pagination from 'components/pagination/Pagination';
import ErrorDisplay from 'components/errorDisplay/ErrorDisplay';
import Menu from 'components/menu/MainMenu';
import { OverlayLoaderComponent } from 'components/loader/Loader';
import SidebarFilter from 'components/sidebarFilter/SidebarFilter';
import { ENTITY_FILTER_TYPES, ENTITY_COLUMNS_TYPES } from 'utils/common';
import { getFilterFromLocalStorage, setFilterToLocalStorage } from 'utils/common';
import ManageColumns from 'components/manageColumns/ManageColumns';
import NoDataFound from 'components/noDataFound/NoDataFound';
import CustomTable from 'components/table/CustomTable';
import { featuresListingColumns } from './variables';
import { formatFeaturesListingData } from 'utils/common';
import { showToast } from 'components/toast/Toast';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { useLocation } from 'react-router-dom';
import { USER_STATUS } from 'constants/filterDropdownData';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';


const PAGE_SIZE = 50;
const today = new Date();
const tenDaysAgo = new Date();
tenDaysAgo.setDate(today.getDate() - 10);

const excludedColumnVisibility = [
  'slug_url',
  'username'
];

export default function FeaturesListing() {
  const { apiCall, loading } = useApi();
  const [featuresData, setFeaturesData] = useState([]);
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [apiError, setApiError] = useState(null);
  const lsFiltersPageSize = getFilterFromLocalStorage(ENTITY_FILTER_TYPES.REPORT_PAGE_SIZE);
  const [pageSize, setPageSize] = useState(
    lsFiltersPageSize && Number.isInteger(lsFiltersPageSize) ? lsFiltersPageSize : PAGE_SIZE
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { search } = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(search));
    const [manageColumnSelectAllCheckbox, setManageColumnSelectAllCheckbox] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedRowsData, setSelectedRowsData] = useState([]);

  const fetchData = useCallback(
    async (text) => {
      const validPageSize = Number.isInteger(pageSize) ? pageSize : PAGE_SIZE;
      try {
        const response = await apiCall.get(`${FEATURES_LISTING_API}`, {
          page: currentPage,
          limit: validPageSize,
          search_text: text,
        });

        const rawData = response?.data?.features || [];
        const formattedData = formatFeaturesListingData(rawData);
        setTotalPages(Math.ceil(rawData.length / pageSize));
        setTotalCount(rawData.length);
        setFeaturesData(formattedData);
      } catch (error) {
        console.error("Error fetching features listing:", error);
        setApiError(error);
      }
    },
    [currentPage, pageSize, searchText]
  );

  const handleRowSelect = useCallback(
    (row) => {
      const findData = selectedRowsData.find((item) => item._id === row._id);
      setSelectedRowsData((prev) => {
        if (findData) {
          return prev.filter((item) => item._id !== findData._id);
        } else {
          return [...prev, row];
        }
      });
    },
    [setSelectedRowsData, selectedRowsData]
  );

  const handleUserAction = async ({ updatedPropertyType, id: selectedId }) => {
    try { 
      history.push(`${updatedPropertyType}?id=${selectedId}`);
    } catch (error) {
      console.error("Error handling user action:", error);
      const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
      showToast({
        message: `Error: ${errorMessage}`,
        success: false,
        customStyle: {
          background: '#F24052',
          color: '#fff',
          padding: '10px',
        },
      });

      if (error.response.status !== 409 && error.response.status !== 400) {
        onClose();
      }
    }
  };

  useEffect(() => {
    if (queryObject?.search_text) {
      setSearchText(queryObject?.search_text);
      handleFetchSearch(queryObject?.search_text);
    } else {
      setSearchText('');
      fetchData();
    }
  }, [currentPage, pageSize, queryObject?.search_text]);

  const columns = useMemo(() => featuresListingColumns, [featuresListingColumns]);
  const data = useMemo(() => {
    return formatFeaturesListingData(featuresData).map(item => ({
      ...item,
    }));
  }, [featuresData]);

  const selectedRowsIds = selectedRowsData.map((item) => item._id);
  const selectedData = useMemo(
    () =>
      selectedRowsIds.map((id) => data.find((row) => row._id === id)),
    [selectedRowsIds, data]
  );


  const lsLocalStorageManageColumns = getFilterFromLocalStorage(ENTITY_COLUMNS_TYPES.FEATURES_LISTING_COLUMNS);

  const [columnVisibility, setColumnVisibility] = useState(
    lsLocalStorageManageColumns
      ? lsLocalStorageManageColumns
      : columns.reduce((acc, column) => {
        acc[column.accessor] = true;
        return acc;
      }, {})
  );

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const handleFetchSearch = useCallback(
    (text) => {
      setCurrentPage(1);
      fetchData(text);
    },
    [fetchData]
  );

  const handleInputFormSubmit = (e) => {
    e.preventDefault();
    handleFetchSearch(searchText);
  };

  useEffect(() => {
    setFilterToLocalStorage(
      ENTITY_COLUMNS_TYPES.FEATURES_LISTING_COLUMNS,
      columnVisibility
    );
  }, [columnVisibility]);

  const visibleColumns = columns.filter(
    (column) => columnVisibility[column.accessor] && !excludedColumnVisibility.includes(column.accessor)
  );

  if (apiError) {
    return <ErrorDisplay error={apiError} />;
  }

  return (
    <>
      <Box height="100vh" bg="#F8FAFB" mr={14} pr={6}>
        <Flex
          justify="space-between"
          align="center"
          p="2"
          boxShadow="sm"
          borderRadius="sm"
          pos="fixed"
          style={{ width: "calc(100vw - 96px)" }}
          zIndex={1000}
        >
          <HStack spacing="4" p={2}>
            <ManageColumns
              columns={columns}
              handleColumnChange={handleColumnChange}
              columnVisibility={columnVisibility}
              setManageColumnSelectAllCheckbox={setManageColumnSelectAllCheckbox}
              manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectAllCheckbox}
            />
            <form onSubmit={handleInputFormSubmit}>
              <InputGroup size="sm">
                <SearchBar
                  background="#F9F9FB"
                  color="#637381"
                  setSearchText={(e) => setSearchText(e.target.value)}
                  searchText={searchText}
                  borderRadius="5px"
                  borderWidth="0.5px"
                  borderColor="#637381"
                />
              </InputGroup>
            </form>
          </HStack>
          <Button
            borderRadius="5px"
            borderColor="#637381"
            background={"linear-gradient(180deg, #22ACEE 0%, #0177B4 100%)"}
            color="#ffffff"
            fontSize="sm"
            colorScheme="teal"
            onClick={() => {
              history.push("/admin/feature");
            }}
          >
            Create Feature
          </Button>
          <Text
            borderRadius="5px"
            borderWidth="0.5px"
            borderColor="#637381"
            background="#F9F9FB"
            color="#637381"
            p={1}
          >
            Total Features: {totalCount}
          </Text>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(value) => setCurrentPage(value)}
            totalCount={totalCount}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setPageSizeToLocalStorage={(value) =>
              setFilterToLocalStorage(
                ENTITY_FILTER_TYPES.REPORT_PAGE_SIZE,
                value
              )
            }
          />
        </Flex>
        <SidebarFilter />
        {featuresData?.length !== 0 ? (
          <>
            <CustomTable
              columns={visibleColumns}
              data={data}
              onSelectedAction={setSelectedAction}
              selectedRowsData={selectedRowsData}
              selectedData={selectedData}
              setSelectedRowsData={setSelectedRowsData}
              selectedRowsIds={selectedRowsIds}
              handleRowSelect={handleRowSelect}
              menuComponent={
                <Menu
                  data={Object.values(USER_STATUS)}
                  onUpdate={() => { }}
                  onUpdatePropertyStatus={handleUserAction}
                  selectedAction={selectedAction}
                  dataType={"Feature"}
                />
              }
            />
          </>
        ) : !loading ? (
          <NoDataFound />
        ) : null}
        <OverlayLoaderComponent isOpen={loading} />
      </Box>
    </>
  );
}
